<template>
  <div class="px-16 py-2 pt-4">
    <div class="my-2">
      <app-logo />
    </div>
    <div class="py-1 pb-2 border-b mb-0.5">
      <div
        class="text-sm"
        v-if="checkDetails(getPartnerRef(details.partnerRef))"
      >
        Payment Confirmation:
        <span class="font-bold">
          {{ getPartnerRef(details.partnerRef) }}
        </span>
      </div>
    </div>
    <div class="mt-6">
      <div>
        <el-table
          class="mt-2"
          :data="transactionSummaryArray"
          border
          style="width: 100%"
        >
          <el-table-column label="Transaction Summary">
            <el-table-column prop="key" />
            <el-table-column prop="value" />
          </el-table-column>
        </el-table>
        <el-table
          class="mt-2"
          :data="transactionBeneficiaryArray"
          border
          style="width: 100%"
        >
          <el-table-column label="Beneficiary Information">
            <el-table-column prop="key" />
            <el-table-column prop="value" />
          </el-table-column>
        </el-table>
        <el-table
          class="mt-2"
          :data="transactionTrackingArray"
          border
          style="width: 100%"
        >
          <el-table-column label="Tracking Information">
            <el-table-column prop="key" />
            <el-table-column prop="value" />
          </el-table-column>
        </el-table>
      </div>
      <div class="my-2 px-2" :class="hasMT103 ? 'mt-28' : 'mt-20'">
        <div class="italic test-xs font-semibold">Disclaimer</div>
        <div style="word-break: normal" class="italic text-xs mt-1">
          This document is strictly confidential communication to and solely for
          the use of the recipient and may not be reproduced or circulated
          without
          <span class="capitalize text-primary">{{ tenantName }} </span>'s'
          prior written consent. If you are not the intended recipient, you may
          not disclose or use the information in this documentation in any way.
        </div>
      </div>
      <div v-if="hasMT103">
        <div class="page-break"></div>
        <div class="my-2 pt-6">
          <app-logo />
        </div>
        <div class="mt_block w-full text-black mt-8">
          <div class="mt_block_bg w-full py-2 px-5 font-black">SWIFT MT103</div>

          <div class="p-5">
            <div
              v-for="(info, index) in details.rawMessage"
              :key="index"
              class="py-1"
            >
              {{ info }}
            </div>
          </div>
        </div>
        <div class="my-2 mt-60">
          <div class="italic test-xs font-semibold">Disclaimer</div>
          <div style="word-break: normal" class="italic text-xs mt-1">
            This document is strictly confidential communication to and solely
            for the use of the recipient and may not be reproduced or circulated
            without
            <span class="capitalize text-primary">{{ tenantName }} </span>'s'
            prior written consent. If you are not the intended recipient, you
            may not disclose or use the information in this documentation in any
            way.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FormatServerName,
  parseBatchDate,
  ParseAmount
} from '@/utils/helpers.js'
import { LOCAL_CURRENCIES, FX_CURRENCIES } from '@/utils/constants'
export default {
  props: {
    transferType: {
      type: String,
      default: ''
    }
  },
  components: {
    AppLogo: () => import('@/assets/images/AppLogo.vue')
  },
  computed: {
    tenantName () {
      return process.env.VUE_APP_TENANT_NAME
    },
    tenantEmail () {
      return process.env.VUE_APP_URL
    },
    details () {
      return this.$store.state.transactions.receiptDetails
    },
    tenantAppLink () {
      return process.env.VUE_APP_URL
    },
    isTransfer () {
      return this.transferType === 'Transfer'
    },
    isPayout () {
      return this.transferType === 'Payout'
    },
    isLocalTransfer () {
      return LOCAL_CURRENCIES.includes(this.details.amount.currency)
    },
    isFXTransfer () {
      return FX_CURRENCIES.includes(this.details.amount.currency)
    },
    transactionSummaryArray () {
      const array = [
        {
          key: 'Sender Name',
          value: this.details.senderName
        },
        {
          key: 'Amount',
          value:
            this.details.amount.symbol +
            this.parseAmount(this.details.amount.amount)
        },
        {
          key: 'Payment Type',
          value: this.getPaymentType(this.details.paymentType)
        },
        {
          key: 'Initiation Date',
          value: this.parseBatchDate(this.details.initiatedAt)
        },
        {
          key: 'Settlement Date',
          value: this.parseBatchDate(this.details.settledAt)
        }
      ]

      let filteredArray = this.filterArray(array)
      if (this.isPayout || this.isLocalTransfer) {
        filteredArray = filteredArray.filter(
          (item) => item.key !== 'Payment Type'
        )
      }
      return filteredArray
    },
    transactionBeneficiaryArray () {
      let array = [
        {
          key: 'Beneficiary Name',
          value: this.details.beneficiaryName
        },
        {
          key: 'Account Number',
          value: this.details.beneficiaryAccNo
        },
        {
          key: 'Swift/BIC',
          value: this.details.beneficiarySwiftBic
        },
        {
          key: 'Payment Reference',
          value: this.details.paymentReference
        }
      ]
      if (this.isLocalTransfer) {
        array = array.filter((item) => item.key !== 'Swift/BIC')
      }
      return this.filterArray(array)
    },
    transactionTrackingArray () {
      const array = [
        {
          key: 'Transfer Method',
          value: this.getTransferMethod(this.details.transferMethod)
        }
      ]

      // Checks if it is an FX Transfer
      if (this.isFXTransfer) {
        // Checks if it was completed via swift or wire Transfer
        if (
          this.details.transferMethod === this.$transactionType.SWIFT_TRANSFER
        ) {
          array.push({
            key: 'UETR',
            value: this.details.uetr
          })
        } else if (
          this.details.transferMethod === this.$transactionType.WIRE_TRANSFER
        ) {
          array.push({
            key: 'IMAD',
            value: this.details.imad
          })
          array.push({
            key: 'OMAD',
            value: this.details.omad
          })
        }
      } else {
        array.push({
          key: 'Session ID',
          value: this.details.sessionID
        })
      }

      return this.filterArray(array)
    },
    hasMT103 () {
      return (
        this.details.transferMethod === this.$transactionType.SWIFT_TRANSFER
      )
    }
  },
  methods: {
    formatServerName (name) {
      return FormatServerName(name)
    },

    parseAmount (amount) {
      return ParseAmount(amount)
    },

    parseBatchDate (date) {
      return parseBatchDate(date)
    },

    checkDetails (detail) {
      return detail != null && detail !== ''
    },

    getPaymentType (type) {
      switch (type) {
        case this.$transactionType.SWIFT_TRANSFER:
          return 'International Wire'
        case this.$transactionType.WIRE_TRANSFER:
          return 'Domestic Wire'
        case this.$transactionType.LOCAL_TRANSFER:
          return 'Local Transfer'
      }
    },

    getTransferMethod (type) {
      switch (type) {
        case this.$transactionType.SWIFT_TRANSFER:
          return 'SWIFT'
        case this.$transactionType.WIRE_TRANSFER:
          return 'FedWire'
        case this.$transactionType.LOCAL_TRANSFER:
          return 'Local'
      }
    },

    // Trying to split the partner reference it's in a format as this (swft_a2njfrfk)
    getPartnerRef (ref) {
      if (!ref) {
        return ''
      }

      const refs = ref.split('_')
      return refs.length > 1 ? refs[1] : refs[0]
    },

    filterArray (array) {
      return array.filter((item) => item.value)
    }
  }
}
</script>

<style scoped>
.page-break {
  page-break-before: always;
}
:deep(.el-table__cell) {
  padding: 0.12rem 0.5rem !important;
  color: black;
}
:deep(.el-table .cell) {
  line-height: 2.5rem;
}

:deep(.el-table__row) {
  margin-bottom: 0.2rem !important;
  color: red !important;
}
.mt_block {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.mt_block_bg {
  background-color: #f6f7fb;
  font-weight: bolder;
  line-height: 2rem;
}
</style>
